import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Select
} from 'antd'
import { FormInstance, ModalProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect, useMemo } from 'react'

import {
  useInvestigationsServiceGetListSpecimensEndpointKey,
  useInvestigationsServiceGetSpecimenEndpoint,
  useInvestigationsServiceSpecimenReceptionEndpoint,
  useInvestigationsServiceUpdateSpecimenEndpoint
} from 'src/api/queries'
import { ApiError, SpecimenLaboratoryStatus } from 'src/api/requests'
import { message, requiredRules, SaveConfirm, Text } from 'src/components'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from './styles'

type Props = {
  onCancel: () => void
  form: FormInstance
  id?: string
} & ModalProps

export const ReceptionModal = ({ onCancel, form, id, ...props }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const queryClient = useQueryClient()

  const { data, isLoading } = useInvestigationsServiceGetSpecimenEndpoint(
    { id: String(id) },
    [String(id)],
    { enabled: !!id }
  )

  const { mutate: reception } =
    useInvestigationsServiceSpecimenReceptionEndpoint({
      onSuccess: () => {
        const values = form.getFieldsValue(true)
        queryClient.invalidateQueries({
          queryKey: [useInvestigationsServiceGetListSpecimensEndpointKey]
        })
        message.success(t('tests.successfully_receptioned'))
        update({
          id: String(id),
          requestBody: {
            comment: values?.comments,
            laboratoryStatus: values?.laboratoryStatus
          }
        })
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const { mutate: update } = useInvestigationsServiceUpdateSpecimenEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useInvestigationsServiceGetListSpecimensEndpointKey]
      })
      onCancel()
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    reception({
      id: String(id),
      requestBody: {
        receivedAt: dayjs(values?.receivedAt).format(),
        temperature: values?.temperature,
        comments: values?.comments
      }
    })
  }

  useEffect(() => {
    form.setFieldsValue({
      receptionDate: dayjs()
    })
  }, [])

  const laboratoryStatusOptions = useMemo(() => {
    return [
      {
        label: t('investigations.conform'),
        value: SpecimenLaboratoryStatus.VALIDATED
      },
      {
        label: t('investigations.not_conform'),
        value: SpecimenLaboratoryStatus.INCONFORM
      }
    ]
  }, [])

  return (
    <Modal
      open
      maskClosable={false}
      centered
      width={520}
      title={t('investigations.investigation_reception')}
      footer={null}
      onCancel={onCancel}
      loading={isLoading}
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Flex vertical gap={24}>
          <Flex vertical gap={4}>
            <Text>{t('common.belongs')}</Text>
            <Text weight={400}>
              {`${data?.person?.firstName} ${data?.person?.lastName}`}
            </Text>
          </Flex>

          <Form.Item name="temperature" label={t('investigations.temperature')}>
            <InputNumber
              className="full-width"
              placeholder={t('investigations.temperature')}
            />
          </Form.Item>

          <Form.Item
            name="receptionDate"
            label={t('investigations.reception_date')}
            rules={requiredRules}
          >
            <DatePicker
              format={dayMonthYearHourMinutesFormatWithDot}
              className="full-width"
              showTime
              needConfirm={false}
            />
          </Form.Item>

          <Form.Item
            name="laboratoryStatus"
            label={t('investigations.investigation_status')}
            rules={requiredRules}
          >
            <Select
              options={laboratoryStatusOptions}
              placeholder={t('investigations.investigation_status')}
            />
          </Form.Item>

          <Form.Item
            name="comments"
            label={t('investigations.cancellation_comments')}
          >
            <Input.TextArea
              className={styles.textarea}
              placeholder={t('investigations.cancellation_comments')}
            />
          </Form.Item>

          <Flex justify="center" gap={16}>
            <Button onClick={onCancel}>{t('common.cancel')}</Button>

            <SaveConfirm title={`${t('roles.confirm')}?`} onConfirm={onSubmit}>
              <Button type="primary">{t('roles.confirm')}</Button>
            </SaveConfirm>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
